/* ------------------------------------------------------------------
   EmailDomainSetupPage.js — public guide for adding the SES DKIM CNAME
   records at common registrars, to verify a custom sending domain.
   Linked from Email Marketing → Settings → "Your own domain".
------------------------------------------------------------------ */
import React from 'react';
import { Link } from 'react-router-dom';

const REGISTRARS = [
  {
    name: 'GoDaddy',
    steps: [
      'Sign in at godaddy.com → click your name → My Products.',
      'Find your domain → click the three dots → Manage DNS (or “DNS”).',
      'In the Records section, click Add New Record → Type: CNAME.',
      'Name (Host): paste the record’s Name, but remove the trailing “.yourdomain.com” — GoDaddy adds your domain automatically (e.g. enter only “abc123._domainkey”).',
      'Value: paste the “…dkim.amazonses.com” value.',
      'Save. Repeat for all 3 records.',
    ],
  },
  {
    name: 'Namecheap',
    steps: [
      'Sign in → Domain List → click Manage next to your domain.',
      'Open the Advanced DNS tab.',
      'Click Add New Record → Type: CNAME Record.',
      'Host: the Name without your domain (e.g. “abc123._domainkey”).',
      'Target: the “…dkim.amazonses.com” value. Leave TTL on Automatic.',
      'Click the green checkmark to save. Repeat for all 3.',
    ],
  },
  {
    name: 'Cloudflare',
    steps: [
      'Log in → select your domain → DNS → Records.',
      'Click Add record → Type: CNAME.',
      'Name: the part before your domain (Cloudflare appends it automatically).',
      'Target: the “…dkim.amazonses.com” value.',
      'IMPORTANT: set Proxy status to “DNS only” (grey cloud, not orange) — proxied CNAMEs break DKIM.',
      'Save. Repeat for all 3.',
    ],
  },
  {
    name: 'Squarespace / Google Domains',
    steps: [
      'Google Domains now lives in Squarespace. Sign in → Settings → Domains → your domain.',
      'Open DNS / DNS Settings → Custom records.',
      'Add a record → Type: CNAME.',
      'Host/Name: the subdomain part of the Name; Data/Value: the amazonses value.',
      'Save. Repeat for all 3.',
    ],
  },
  {
    name: 'Bluehost',
    steps: [
      'Log in → Domains → select your domain → DNS / Zone Editor.',
      'Under CNAME records, click Add Record.',
      'Host Record: the Name part; Points To: the amazonses value; TTL: default.',
      'Save. Repeat for all 3.',
    ],
  },
  {
    name: 'Wix',
    steps: [
      'Wix dashboard → Settings → Domains → select your domain → Manage DNS Records (or Advanced).',
      'Under CNAME (Aliases), click + Add Record.',
      'Host Name: the Name part; Value: the amazonses value.',
      'Save. Repeat for all 3.',
    ],
  },
  {
    name: 'Hover',
    steps: [
      'Sign in → click your domain → open the DNS tab.',
      'Click Add a Record → Type: CNAME.',
      'Hostname: the Name part; Target Host: the amazonses value.',
      'Save. Repeat for all 3.',
    ],
  },
  {
    name: 'AWS Route 53',
    steps: [
      'Open Route 53 → Hosted zones → select your domain.',
      'Create record → Record type: CNAME.',
      'Record name: paste the full Name (Route 53 does NOT auto-append — keep the whole thing).',
      'Value: the amazonses value. Save. Repeat for all 3.',
    ],
  },
  {
    name: 'Other / generic',
    steps: [
      'Log in to wherever your domain’s DNS is managed (your registrar or hosting control panel).',
      'Find DNS, Zone Editor, or Advanced DNS.',
      'Add 3 records of type CNAME using the Name and Value shown in your Greatly Social settings.',
      'If the host auto-appends your domain, omit the trailing “.yourdomain.com” in the Name. Use default/automatic TTL.',
    ],
  },
];

export default function EmailDomainSetupPage() {
  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: '40px 20px 80px', color: '#111827' }}>
      <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#2563eb', marginBottom: 8 }}>Email Marketing</div>
      <h1 style={{ fontSize: 30, fontWeight: 800, margin: '0 0 12px' }}>Verify your sending domain</h1>
      <p style={{ fontSize: 16, color: '#4b5563', lineHeight: 1.6, margin: '0 0 28px' }}>
        Sending from your own domain gives your emails the best deliverability and full branding. To prove you own the domain,
        you’ll add <strong>3 CNAME records</strong> (provided in your <Link to="/dashboard/marketing/email/settings" style={{ color: '#2563eb' }}>Email Settings</Link>) to your DNS. It takes about 5 minutes.
      </p>

      <Callout>
        <strong>Where do the 3 records come from?</strong> In Greatly Social, go to <em>Marketing → Email Marketing → Settings → “Your own domain”</em>, enter your domain, and you’ll see the 3 CNAME records to copy. Keep that tab open while you follow the steps below.
      </Callout>

      {/* what you'll do */}
      <h2 style={h2}>The idea</h2>
      <p style={p}>
        Each record has a <strong>Name</strong> (sometimes called Host) and a <strong>Value</strong> (sometimes “Points to” or “Target”).
        You add all three as type <strong>CNAME</strong>. Once your DNS provider has them, Greatly Social detects them automatically —
        come back and click <strong>Check verification</strong>.
      </p>

      {/* per-registrar */}
      <h2 style={h2}>Step-by-step for your provider</h2>
      <p style={{ ...p, marginBottom: 16 }}>Find your domain registrar or DNS host below. Not sure who that is? It’s usually where you bought the domain.</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {REGISTRARS.map(r => (
          <details key={r.name} style={{ border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff', padding: '4px 18px' }}>
            <summary style={{ cursor: 'pointer', fontSize: 16, fontWeight: 700, padding: '14px 0', listStyle: 'none' }}>
              {r.name}
            </summary>
            <ol style={{ margin: '0 0 16px', paddingLeft: 22, color: '#374151', fontSize: 15, lineHeight: 1.7 }}>
              {r.steps.map((s, i) => <li key={i} style={{ marginBottom: 6 }}>{s}</li>)}
            </ol>
          </details>
        ))}
      </div>

      {/* gotchas */}
      <h2 style={h2}>Common gotchas</h2>
      <ul style={{ ...p, paddingLeft: 22, lineHeight: 1.8 }}>
        <li><strong>The trailing domain.</strong> Most hosts auto-append your domain to the Name. If so, enter the Name <em>without</em> the trailing “.yourdomain.com”. (Exception: AWS Route 53 — keep the full Name.)</li>
        <li><strong>Cloudflare proxy.</strong> Set the record to “DNS only” (grey cloud). Proxied (orange) breaks DKIM.</li>
        <li><strong>Add all three.</strong> All 3 records are required — DKIM rotates between them.</li>
        <li><strong>Give it time.</strong> DNS changes are usually live in a few minutes, but can take up to a few hours.</li>
        <li><strong>Don’t change the values.</strong> Copy/paste exactly — a single wrong character means it won’t verify.</li>
      </ul>

      <div style={{ marginTop: 28, padding: '18px 20px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12 }}>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Last step</div>
        <p style={{ margin: 0, color: '#374151', fontSize: 15, lineHeight: 1.6 }}>
          After you’ve added all 3 records, return to <Link to="/dashboard/marketing/email/settings" style={{ color: '#2563eb', fontWeight: 600 }}>Email Settings</Link> and click <strong>Check verification</strong>. Once it’s verified, pick the address you want to send from (e.g. <code>hello@yourdomain.com</code>) and you’re live.
        </p>
      </div>
    </div>
  );
}

const h2 = { fontSize: 20, fontWeight: 700, margin: '32px 0 10px' };
const p  = { fontSize: 15, color: '#374151', lineHeight: 1.7, margin: '0 0 10px' };
const Callout = ({ children }) => (
  <div style={{ padding: '16px 18px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 12, fontSize: 15, color: '#374151', lineHeight: 1.6, marginBottom: 8 }}>{children}</div>
);
